import axios from 'axios';
import { extract_error_message, parse_response_data } from '@/utils/api_response_helpers';
import {
  ASSIGN_WORKFLOW_TO_ENTITY_TYPE_TEMPLATE_URL,
  GET_TEMPLATE_ENTITY_TYPE_ASSIGNMENTS_URL,
  GET_WORKFLOWS_ASSIGNED_TO_ENTITY_TYPE_TEMPLATE_URL,
  LIST_WORKFLOW_MANAGEMENT_URL,
  get_request_config,
} from '@/utils/system_constants/constant';
import {
  extract_template_entity_type_identifier,
  extract_workflow_identifier,
  normalize_identifier,
  normalize_workflow,
  resolve_template_identifier,
} from './esigning_setup_helpers';

const esigning_setup_workflow_methods = {
  get_current_template_entity_type_id() {
    return Number(
      extract_template_entity_type_identifier(this.active_template_entity_type_assignment) ||
      this.route_template_entity_type_id ||
      0,
    ) || null;
  },

  assign_workflow(workflow_id) {
    this.assign_workflow_to_template(workflow_id);
  },

  format_date(value) {
    if (!value) return 'Recently updated';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Recently updated';

    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
    }).format(date);
  },

  workflow_step_summary(workflow) {
    const step_count = Number(workflow?.step_count || 0) || 0;
    const signer_count = Number(workflow?.total_expected_signers || 0) || 0;
    const step_label = `${step_count} step${step_count === 1 ? '' : 's'}`;
    const signer_label = `${signer_count} signer${signer_count === 1 ? '' : 's'}`;
    return `${step_label} Â· ${signer_label}`;
  },

  is_workflow_assigned_to_template(workflow_id) {
    if (!this.assigned_workflow_id) return false;
    return this.assigned_workflow_id === String(workflow_id || '').trim();
  },

  normalize_template_entity_type_assignment(item) {
    if (!item || typeof item !== 'object') return null;

    const assignment_id = extract_template_entity_type_identifier(item);
    if (!assignment_id) return null;

    return {
      ...item,
      id: assignment_id,
      template_id: resolve_template_identifier(item),
      template_document_entity_type_id: normalize_identifier(item.template_document_entity_type_id),
      template_entity_type_id: normalize_identifier(item.template_entity_type_id),
      workflow_id: extract_workflow_identifier(item),
      is_assigned: item.is_assigned !== false,
    };
  },

  async load_template_entity_type_assignments() {
    const template_id = Number(this.template_id || 0) || null;

    if (!template_id) {
      this.template_entity_type_assignments = [];
      this.assigned_workflow_binding = null;
      this.selected_workflow_id = '';
      return;
    }

    try {
      const response = await axios.get(
        GET_TEMPLATE_ENTITY_TYPE_ASSIGNMENTS_URL,
        get_request_config({
          params: { template_id },
        }),
      );

      const parsed = parse_response_data(response);
      const raw_assignment_items = Array.isArray(parsed?.data) ? parsed.data : [];
      const assignment_items = raw_assignment_items.filter((item) => {
        const requested_assignment_id = String(this.route_template_entity_type_id || '').trim();
        const item_assignment_id = extract_template_entity_type_identifier(item);

        if (requested_assignment_id) {
          return item_assignment_id === requested_assignment_id;
        }

        const item_template_id = resolve_template_identifier(item);
        if (!item_template_id) return true;
        return item_template_id === template_id;
      });

      this.template_entity_type_assignments = assignment_items
        .map((item) => this.normalize_template_entity_type_assignment(item))
        .filter(Boolean);

      const active_assignment = this.active_template_entity_type_assignment;
      if (active_assignment?.workflow_id) {
        this.selected_workflow_id = String(active_assignment.workflow_id);
        return;
      }

      if (this.route_workflow_id) {
        this.selected_workflow_id = String(this.route_workflow_id);
        return;
      }

      this.assigned_workflow_binding = null;
      this.selected_workflow_id = '';
    } catch (error) {
      this.template_entity_type_assignments = [];
      this.assigned_workflow_binding = null;
      this.selected_workflow_id = '';
      this.show_error(
        extract_error_message(error, 'Failed to load template entity type assignments.'),
      );
    }
  },

  async load_assigned_workflow_binding() {
    const template_document_entity_type_id = this.get_current_template_entity_type_id();

    if (!template_document_entity_type_id) {
      this.assigned_workflow_binding = null;
      this.selected_workflow_id = extract_workflow_identifier(this.active_template_entity_type_assignment)
        || this.route_workflow_id;
      return;
    }

    try {
      const response = await axios.post(
        GET_WORKFLOWS_ASSIGNED_TO_ENTITY_TYPE_TEMPLATE_URL,
        { template_document_entity_type_id },
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      const items = (Array.isArray(parsed?.data) ? parsed.data : []).filter((item) => {
        const binding_assignment_id = Number(
          extract_template_entity_type_identifier(item) || 0,
        ) || 0;
        return binding_assignment_id === template_document_entity_type_id;
      });

      const active_binding = items.find((item) => item?.is_assigned !== false) || null;
      this.assigned_workflow_binding = active_binding;

      if (extract_workflow_identifier(active_binding)) {
        this.selected_workflow_id = extract_workflow_identifier(active_binding);
        return;
      }

      this.selected_workflow_id = extract_workflow_identifier(this.active_template_entity_type_assignment);
      if (!this.selected_workflow_id && this.route_workflow_id) {
        this.selected_workflow_id = this.route_workflow_id;
      }
    } catch (error) {
      this.assigned_workflow_binding = null;
      this.selected_workflow_id = extract_workflow_identifier(this.active_template_entity_type_assignment)
        || this.route_workflow_id;
      this.show_error(
        extract_error_message(error, 'Failed to load assigned workflows for this template.'),
      );
    }
  },

  async load_workflows() {
    this.workflows_loading = true;

    try {
      await this.load_template_entity_type_assignments();
      await this.load_assigned_workflow_binding();

      const response = await axios.get(
        LIST_WORKFLOW_MANAGEMENT_URL,
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      const workflows = Array.isArray(parsed?.workflows) ? parsed.workflows : [];

      this.workflows = workflows
        .map(normalize_workflow)
        .filter(Boolean);

      const has_selected_workflow = this.workflows.some(
        (workflow) => workflow.id === this.selected_workflow_id,
      );

      if (!has_selected_workflow) {
        this.selected_workflow_id = this.assigned_workflow_id || '';
      }
    } catch (error) {
      this.workflows = [];
      this.selected_workflow_id = '';
      this.assigned_workflow_binding = null;
      this.show_error(
        extract_error_message(error, 'Failed to load workflows.'),
      );
    } finally {
      this.workflows_loading = false;
    }
  },

  async assign_workflow_to_template(workflow_id) {
    const template_document_entity_type_id = this.get_current_template_entity_type_id();
    const normalized_workflow_id = Number(workflow_id || 0) || null;

    if (!template_document_entity_type_id) {
      this.show_warning(
        'Workflow Assignment',
        'Missing template entity type context for this template.',
      );
      return;
    }

    if (!normalized_workflow_id) {
      this.show_warning(
        'Workflow Assignment',
        'Please select a valid workflow.',
      );
      return;
    }

    if (this.assigning_workflow_id) return;

    this.assigning_workflow_id = String(workflow_id);
    const loader = await this.show_loader('Assigning workflow...');

    try {
      const response = await axios.post(
        ASSIGN_WORKFLOW_TO_ENTITY_TYPE_TEMPLATE_URL,
        {
          template_document_entity_type_id,
          workflow_id: normalized_workflow_id,
          is_assigned: true,
        },
        get_request_config(),
      );

      const parsed = parse_response_data(response);
      if (String(parsed?.status || 'success').toLowerCase() !== 'success') {
        this.show_error(parsed?.message || 'Failed to assign workflow to template.');
        return;
      }

      this.selected_workflow_id = String(workflow_id);
      await this.load_template_entity_type_assignments();
      await this.load_assigned_workflow_binding();
      this.set_active_tab('workflows');
      this.show_success(parsed?.message || 'Workflow assignment updated successfully');
    } catch (error) {
      this.show_error(
        extract_error_message(error, 'Failed to assign workflow to template.'),
      );
    } finally {
      await this.close_loader(loader);
      this.assigning_workflow_id = '';
    }
  },
};

export default esigning_setup_workflow_methods;
